import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import * as CompareProductsActions from './compare-products.action';
import * as SearchSelectors from './compare-products.selectors';
import { combineLatest, filter, map, Observable, take } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { globalToastKey, maximumNumberOfProductsToCompare } from '@cpak/consts';
import { MessageService } from 'primeng/api';
import { AttributeTypeIds, ProductModel } from '@cpak/adp-cpak-products-service-models';
import { ProductDataForBar } from './compare-products.models';
import { UserPreferencesFacade } from '../../global-config-store/state/user-preferences/user-preferences.facade';
import { getImageFromCatalogAssets, getRatingAttributeValue } from '@cpak/shared/utils';
import { UntilDestroy } from '@ngneat/until-destroy';
import { PlantsEnum } from '@cpak/adp-cpak-quotes-service-models';

@UntilDestroy()
@Injectable()
export class CompareProductsFacade {
  constructor(
    private readonly store: Store,
    private messageService: MessageService,
    private transloco: TranslocoService,
    private userPreferencesFacade: UserPreferencesFacade
  ) {}

  public getProductsDataForBar(): Observable<ProductDataForBar[]> {
    return this.store.pipe(select(SearchSelectors.getProductsDataForBar()));
  }

  public mapProductDataAndAddForCompare(product: ProductModel): void {
    combineLatest([
      this.userPreferencesFacade.getPartNumberDisplay(),
      this.getProductsDataForBar(),
      this.userPreferencesFacade.getPlant(),
    ])
      .pipe(take(1))
      .subscribe(([partNumberIdToDisplay, productsDataForBar, plant]) => {
        if (productsDataForBar.length >= maximumNumberOfProductsToCompare) {
          this.showMaximumNumberOfProductsToCompareMessage();
        } else {
          const productDataForBar = this.getProductDataForBar(product, partNumberIdToDisplay, plant);
          this.store.dispatch(CompareProductsActions.addProductDataForBar({ productDataForBar }));
        }
      });
  }

  public addMappedProductDataForCompare(productDataForBar: ProductDataForBar): void {
    this.getProductsDataForBar()
      .pipe(take(1))
      .subscribe(productsDataForBar => {
        if (productsDataForBar.length >= maximumNumberOfProductsToCompare) {
          this.showMaximumNumberOfProductsToCompareMessage();
        } else {
          this.store.dispatch(CompareProductsActions.addProductDataForBar({ productDataForBar }));
        }
      });
  }

  public removeProductFromComparison(productId: string): void {
    this.store.dispatch(CompareProductsActions.removeProductFromComparison({ productId }));
  }

  public removeAllProductsFromComparison(): void {
    this.store.dispatch(CompareProductsActions.removeAllProductsFromComparison());
  }

  public isProductAddedToCompare(productId: string | undefined): Observable<boolean> {
    return this.getProductsDataForBar().pipe(
      filter(productsDataForBar => !!productsDataForBar && !!productId),
      map(productsDataForBar => !!productsDataForBar.find(productData => productData.id === productId))
    );
  }

  private showMaximumNumberOfProductsToCompareMessage(): void {
    const message = this.transloco.translate('maxCompareNotification');
    this.messageService.add({ severity: 'custom-info', summary: message, key: globalToastKey });
  }

  private getProductDataForBar(
    product: ProductModel,
    partNumberIdToDisplay: AttributeTypeIds,
    plant: PlantsEnum | undefined
  ): ProductDataForBar {
    return {
      id: product.id,
      name: product.name,
      iconSrc: this.getProductImageUrl(product),
      rating: getRatingAttributeValue({
        attributes: product.attributes,
        ratingType: AttributeTypeIds.PartRating,
        plant,
      }),
      productNumber: this.getProductNumber(product, partNumberIdToDisplay),
      vendor: this.getVendorName(product),
    };
  }

  private getProductImageUrl(product: ProductModel): string {
    const size = '48';
    const URLObject = new URL(getImageFromCatalogAssets(product?.assets));
    URLObject.searchParams.append('width', size);
    URLObject.searchParams.append('height', size);

    return URLObject.href;
  }

  private getProductNumber(product: ProductModel, productNumberAttributeId: string): string {
    return product.attributes.find(attr => attr.attributeTypeId === productNumberAttributeId)?.value || '';
  }

  private getVendorName(product: ProductModel): string {
    return product.attributes.find(attr => attr.attributeTypeId === AttributeTypeIds.Vendor)?.value || '';
  }
}
